import type { MarketplaceProduct, TreatmentOffer } from "@/types/api";
import {
  cartItemFromProduct,
  type PlanFrequency,
} from "@/features/marketplace/stores/cart";

export function canResumeTreatmentOffer(offer: TreatmentOffer | undefined) {
  return Boolean(
    offer?.can_resume &&
      ["offered", "purchase_started", "paid"].includes(offer.status),
  );
}

/** This checkout buys the clinician's exact authorization, not a Shop dose
 * experiment offer. Keep its displayed plan pricing on the same base listing
 * that the authorization-only purchase request sends to the backend. */
export function treatmentOfferCartProduct(product: MarketplaceProduct) {
  return {
    ...cartItemFromProduct({ ...product, dose_offers: undefined }),
    quantity: 1,
  };
}

export function marketplaceProductForTreatmentOffer(
  products: MarketplaceProduct[],
  offer: TreatmentOffer | undefined,
) {
  if (!offer) return undefined;
  if (offer.sanity_product_id) {
    const exact = products.find(
      (product) => product.id === offer.sanity_product_id,
    );
    return exact?.canonical_product_id === offer.purchase_product_id
      ? exact
      : undefined;
  }
  const matches = products.filter(
    (product) =>
      product.canonical_product_id === offer.purchase_product_id ||
      product.id === offer.purchase_product_id,
  );
  return matches.length === 1 ? matches[0] : undefined;
}

/** A clinician authorization must always be purchased alone. The backend
 * revalidates this exact one-line shape before charging or bypassing gates. */
export function treatmentOfferPurchasePayload({
  sanityProductId,
  offerId,
  frequency,
  paymentMethodId,
}: {
  sanityProductId: string;
  offerId: string;
  frequency: PlanFrequency;
  paymentMethodId: string;
}) {
  return {
    items: [
      {
        sanity_product_id: sanityProductId,
        frequency,
        clinical_authorization_id: offerId,
      },
    ],
    payment_method_id: paymentMethodId,
  };
}
